import React from 'react';
import { useMediaQuery, useTheme, styled, Box, alpha } from '@mui/material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';

interface EmptyStateProps {
    title: string;
    description?: string;
    icon?: React.ReactNode;
    showParkingAnimation?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
    alignItems: 'center',
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
}));

const BackgroundContainer = styled('div')({
    height: '100%',
    opacity: 0.3,
    position: 'absolute',
    width: '100%',
    '& > div': {
        borderRadius: '50%',
        filter: 'blur(60px)',
        position: 'absolute',
    },
});

const BackgroundBlob1 = styled(Box)(({ theme }) => ({
    animation: 'float 6s ease-in-out infinite',
    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.3)} 0%, transparent 70%)`,
    height: '300px',
    right: '-50px',
    top: '-100px',
    width: '300px',
    '@keyframes float': {
        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
        '50%': { transform: 'translate(-20px, 20px) scale(1.1)' },
    },
}));

const BackgroundBlob2 = styled(Box)(({ theme }) => ({
    animation: 'float 8s ease-in-out infinite',
    animationDelay: '1s',
    background: `radial-gradient(circle, ${alpha(theme.palette.primary.dark, 0.25)} 0%, transparent 70%)`,
    bottom: '-80px',
    height: '250px',
    left: '-60px',
    width: '250px',
    '@keyframes float': {
        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
        '50%': { transform: 'translate(-20px, 20px) scale(1.1)' },
    },
}));

const BackgroundBlob3 = styled(Box)(({ theme }) => ({
    animation: 'float 7s ease-in-out infinite',
    animationDelay: '2s',
    background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 70%)`,
    height: '200px',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '200px',
    '@keyframes float': {
        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
        '50%': { transform: 'translate(-20px, 20px) scale(1.1)' },
    },
}));

const MainContent = styled(Box)<{ isMobile: boolean }>(({ isMobile, theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    maxWidth: '600px',
    padding: isMobile ? theme.spacing(4, 3) : theme.spacing(6, 4),
    position: 'relative',
    zIndex: 1,
}));

const AnimationContainer = styled(Box)<{ isMobile: boolean }>(({ isMobile }) => ({
    alignItems: 'center',
    display: 'flex',
    height: isMobile ? '140px' : '180px',
    justifyContent: 'center',
    position: 'relative',
    width: isMobile ? '200px' : '280px',
}));

const FloatingIconContainer = styled(Box)<{ isMobile: boolean }>(({ isMobile, theme }) => ({
    alignItems: 'center',
    animation: 'levitate 3s ease-in-out infinite',
    backdropFilter: 'blur(20px)',
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.dark, 0.2)} 100%)`,
    border: `2px solid ${alpha(theme.palette.text.primary, 0.1)}`,
    borderRadius: '50%',
    display: 'flex',
    height: isMobile ? '80px' : '100px',
    justifyContent: 'center',
    position: 'absolute',
    width: isMobile ? '80px' : '100px',
    '@keyframes levitate': {
        '0%, 100%': { transform: 'translateY(0px)' },
        '50%': { transform: 'translateY(-15px)' },
    },
}));

const CustomIconContainer = styled(Box)<{ isMobile: boolean }>(({ isMobile, theme }) => ({
    alignItems: 'center',
    animation: 'levitate 3s ease-in-out infinite',
    backdropFilter: 'blur(20px)',
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.dark, 0.2)} 100%)`,
    border: `2px solid ${alpha(theme.palette.text.primary, 0.1)}`,
    borderRadius: '50%',
    display: 'flex',
    height: isMobile ? '80px' : '100px',
    justifyContent: 'center',
    width: isMobile ? '80px' : '100px',
    '@keyframes levitate': {
        '0%, 100%': { transform: 'translateY(0px)' },
        '50%': { transform: 'translateY(-15px)' },
    },
}));

const StyledCarIcon = styled(DirectionsCarIcon)<{ isMobile: boolean }>(({ isMobile, theme }) => ({
    color: theme.palette.text.primary,
    fontSize: isMobile ? '2.5rem' : '3rem',
    opacity: 0.6,
}));

const TextContent = styled(Box)<{ isMobile: boolean }>(({ isMobile, theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    maxWidth: isMobile ? '90%' : '550px',
    textAlign: 'center',
}));

const Title = styled('h2')<{ isMobile: boolean }>(({ isMobile, theme }) => ({
    color: theme.palette.text.primary,
    fontSize: isMobile ? theme.typography.h5.fontSize : theme.typography.h4.fontSize,
    fontWeight: theme.typography.h4.fontWeight,
    letterSpacing: '-0.02em',
    lineHeight: 1.4,
    margin: 0,
}));

const Description = styled('p')<{ isMobile: boolean }>(({ isMobile, theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: isMobile ? theme.typography.body2.fontSize : theme.typography.body1.fontSize,
    fontWeight: theme.typography.body1.fontWeight,
    lineHeight: 1.6,
    margin: 0,
}));

const EmptyState: React.FC<EmptyStateProps> = ({
    title,
    description,
    icon,
    showParkingAnimation = true
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Container>
            {/* Animated background elements */}
            <BackgroundContainer>
                <BackgroundBlob1 />
                <BackgroundBlob2 />
                <BackgroundBlob3 />
            </BackgroundContainer>

            {/* Main content */}
            <MainContent isMobile={isMobile}>
                {/* Animated illustration */}
                {showParkingAnimation && (
                    <AnimationContainer isMobile={isMobile}>
                        {/* Floating icon */}
                        <FloatingIconContainer isMobile={isMobile}>
                            {icon || <StyledCarIcon isMobile={isMobile} />}
                        </FloatingIconContainer>
                    </AnimationContainer>
                )}

                {/* Custom icon without animation */}
                {!showParkingAnimation && icon && (
                    <CustomIconContainer isMobile={isMobile}>
                        {icon}
                    </CustomIconContainer>
                )}

                {/* Text content */}
                <TextContent isMobile={isMobile}>
                    <Title isMobile={isMobile}>{title}</Title>
                    {description && (
                        <Description isMobile={isMobile}>{description}</Description>
                    )}
                </TextContent>
            </MainContent>
        </Container>
    );
};

export default EmptyState;
